import { PlayerWrapper } from "@infinitaslearning/app-player";
import { observer } from "mobx-react";
import type React from "react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Box } from "styles/helpers/layout";

import type { FlashcardRendererProps } from "./FlashcardRenderer.types";

/**
 *  This component displays a set of flashcards using the Player's flashcard
 * drilling component.
 *
 *  Note that this currently shows all the words in a random order and all cards
 * are shown twice, so it's expected that you see 0/2 in the progress bar even if
 * there's only one card available in the input data.
 *
 *  For the time being this is NOT configurable, so we cannot avoid repetition
 * unless we decide upon generating a manual renderer with custom navigation.
 *
 * @see https://app-player-documentation.infinicloud.app/?path=/docs/contextual-players-general-drilling-player-flashcard-drilling--docs
 */
export const FlashcardPracticeRenderer: React.FC<FlashcardRendererProps> = observer(
  ({ contentSets = [] }) => {
    const { t } = useTranslation("homepage");

    // For flashcards to properly show up in the Player drilling flashcard component,
    // we need to merge all content sets in a single array, where each of its items
    // should be an array of particles representing one card
    const flashcardContent = useMemo(
      () => contentSets.map(({ content }) => content),
      [contentSets],
    );

    if (!flashcardContent.length) {
      return null;
    }

    // Player's flashcard drilling component shows all the words in a random order
    // and all cards are shown twice (so it's expected that you see 0/2 in the
    // progress bar even if there's only one card available)
    return (
      <Box data-cy="edify-flashcard-practice-container">
        <PlayerWrapper
          id="flashcard-exercise-player"
          context="drilling"
          // @ts-ignore
          initialData={{ topics: flashcardContent }}
          config={{
            translations: {
              // Text shown above progress bar
              drilling_progress_bar_question: t(
                "edifyTopicDetails.flashcardRenderer.progressBarTitle",
              ),
              // Button to display cards back side (definition)
              drilling_flashcard_show_definitions: t(
                "edifyTopicDetails.flashcardRenderer.showDefinitions",
              ),
              // Button to display cards front side (term)
              drilling_flashcard_show_terms: t("edifyTopicDetails.flashcardRenderer.showTerms"),
              // Button to flip card
              turn: t("edifyTopicDetails.flashcardRenderer.flipCard"),
              // Button to restart flashcard exercise
              drilling_flashcard_flashcard_redo_button: t(
                "edifyTopicDetails.flashcardRenderer.redoPractice",
              ),
              // Button to navigate to next card
              drilling_go_to_next_flashcard: t("edifyTopicDetails.flashcardRenderer.goToNextCard"),
              // Congrats message upon finishing
              drilling_flashcard_finish_title: t(
                "edifyTopicDetails.flashcardRenderer.finishPractice.title",
              ),
              drilling_flashcard_finish_subtitle: t(
                "edifyTopicDetails.flashcardRenderer.finishPractice.subtitle",
              ),
            },
            // Don't show button to exit practice, as it will do nothing
            disableDrillingFinishButton: true,
            superSubmitButton: null,
          }}
        />
      </Box>
    );
  },
);
